import { FC, useEffect, useMemo, useRef } from 'react';
import { blue, gray, green, red, white } from '@carbon/colors';
import { atom, useAtomValue } from 'jotai';
import { Layer, Source, type MapRef } from 'react-map-gl';
import bbox from '@turf/bbox';
import Map from 'components/map/map';
import { casesCoverageMapConfigAtom } from 'lib/evaluation-case/map-config';
import type { EvaluationCaseParams } from './atoms';
import styles from './evaluation-cases-map-strip.module.scss';

const coverageMapRefAtom = atom<MapRef | null>(null);

const SOURCE_LAYER = 'evaluation-cases';
const SOURCE_ID = 'evaluation-cases-coverage-tiles';
// The union of every case's footprints: a near-clear fill, only so a click anywhere in it hits.
const CLICK_LAYER = 'ec-cov-shape-fill';

const isType = (type: string) => ['==', ['get', 'type'], type];
const isState = (state: string) => ['==', ['get', 'coverage_state'], state];

/**
 * Builds the MVT tile URL with filter params as query string.
 * @param {EvaluationCaseParams} params - Current list filter params
 * @return {string} Tile URL template with {x}-{y}-{z} placeholder
 */
export const buildTileUrl = (params: EvaluationCaseParams): string => {
  const qs = new URLSearchParams();

  if (params.text) qs.set('text', params.text);
  if (params.evaluationType?.length) {
    params.evaluationType.forEach((t) => qs.append('evaluation_type[]', t));
  }
  if (params.createdBy?.length) {
    params.createdBy.forEach((id) => qs.append('created_by[]', id));
  }
  if (params.createdAt?.start)
    qs.set('created_at[start]', params.createdAt.start);
  if (params.createdAt?.end) qs.set('created_at[end]', params.createdAt.end);
  if (params.updatedAt?.start)
    qs.set('updated_at[start]', params.updatedAt.start);
  if (params.updatedAt?.end) qs.set('updated_at[end]', params.updatedAt.end);

  const base = `${window.location.origin}/maps/tiles/evaluation_cases/{x}-{y}-{z}`;
  const query = qs.toString();
  return query ? `${base}?${query}` : base;
};

/**
 * The tile URL, versioned with the cases' latest coverage_computed_at (`_v`) so a browser-cached
 * tile is refetched once any case's coverage is recomputed. The server ignores `_v`.
 *
 * @param {EvaluationCaseParams} params - The list's filters.
 * @param {string | null} version - The list response's coverageVersion.
 * @return {string} The URL template.
 */
export const buildCoverageTileUrl = (
  params: EvaluationCaseParams,
  version: string | null
): string => {
  const base = buildTileUrl(params);
  if (!version) return base;

  return `${base}${base.includes('?') ? '&' : '?'}_v=${encodeURIComponent(
    version
  )}`;
};

export interface EvaluationCasesCoverageMapStripProps {
  params: EvaluationCaseParams;
  /** The list response's coverageVersion: the latest coverage_computed_at. */
  coverageVersion: string | null;
  selectedCaseId: number | null;
  selectedBounds: [[number, number], [number, number]] | null;
  onMapClick?: (evaluationCaseId: number) => void;
}

/**
 * The Cases list map: each case's cached coverage
 * (Map::Tiles::EvaluationCases). A complete case is its coverage in green with a solid outline;
 * an incomplete one is the union of its assigned footprints, dashed; a complete one whose
 * footprints share no area ('empty') is that union dashed in red. Every built-in layer starts off.
 *
 * @param {EvaluationCasesCoverageMapStripProps} props - Filters, version, selection and click handler.
 * @return {JSX.Element} The map strip.
 */
const EvaluationCasesCoverageMapStrip: FC<
  EvaluationCasesCoverageMapStripProps
> = ({
  params,
  coverageVersion,
  selectedCaseId,
  selectedBounds,
  onMapClick,
}) => {
  const mapRef = useAtomValue(coverageMapRefAtom);
  const hasFitted = useRef(false);

  // The fit follows the filters only: a new version (a recomputed case) reloads the tiles in place
  // without moving the map.
  const filterUrl = useMemo(() => buildTileUrl(params), [params]);
  const tiles = useMemo(
    () => [buildCoverageTileUrl(params, coverageVersion)],
    [params, coverageVersion]
  );

  // Fit to the rendered cases once their tiles paint (queryRenderedFeatures is empty until then).
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    hasFitted.current = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tryFit = () => {
      if (hasFitted.current || attempts > 10) return;
      attempts += 1;
      const features = map.queryRenderedFeatures(undefined, {
        layers: [CLICK_LAYER],
      });
      if (features.length === 0) {
        timer = setTimeout(tryFit, 500);
        return;
      }
      hasFitted.current = true;
      const [minLng, minLat, maxLng, maxLat] = bbox({
        type: 'FeatureCollection',
        features: features as unknown as GeoJSON.Feature[],
      });
      if ([minLng, minLat, maxLng, maxLat].every(Number.isFinite)) {
        map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 40, maxZoom: 12, duration: 500 }
        );
      }
    };
    timer = setTimeout(tryFit, 1000);
    return () => clearTimeout(timer);
  }, [mapRef, filterUrl]);

  // A row click fits to the case's coverage (its coverageBbox).
  useEffect(() => {
    if (!mapRef || !selectedBounds) return;
    mapRef
      .getMap()
      ?.fitBounds(selectedBounds, { padding: 40, maxZoom: 14, duration: 600 });
  }, [mapRef, selectedBounds]);

  // The tiles carry evaluation_case_id as a number, so the selection compares as one.
  const highlightFilter = useMemo(
    () => [
      'all',
      ['in', ['get', 'type'], ['literal', ['shape', 'coverage']]],
      ['==', ['get', 'evaluation_case_id'], selectedCaseId ?? -1],
    ],
    [selectedCaseId]
  );

  return (
    <div className={styles.mapContainer} data-testid="cases-coverage-map">
      <Map
        className={styles.mapFill}
        longitude={-98}
        latitude={31}
        zoom={4}
        mapConfigAtom={casesCoverageMapConfigAtom}
        refAtom={coverageMapRefAtom}
        showControls
        showMapFilters
        interactiveLayerIds={[CLICK_LAYER]}
        onClick={(event) => {
          const id = event.features?.[0]?.properties?.evaluation_case_id;
          if (id && onMapClick) onMapClick(Number(id));
        }}
        cursor="pointer"
      >
        <Source id={SOURCE_ID} type="vector" tiles={tiles}>
          <Layer
            id={CLICK_LAYER}
            type="fill"
            source-layer={SOURCE_LAYER}
            filter={isType('shape') as never}
            paint={{ 'fill-color': gray[50], 'fill-opacity': 0.05 }}
          />
          <Layer
            id="ec-cov-coverage-fill"
            type="fill"
            source-layer={SOURCE_LAYER}
            filter={['all', isType('coverage'), isState('complete')] as never}
            paint={{ 'fill-color': green[50], 'fill-opacity': 0.35 }}
          />
          <Layer
            id="ec-cov-coverage-line"
            type="line"
            source-layer={SOURCE_LAYER}
            filter={['all', isType('coverage'), isState('complete')] as never}
            paint={{ 'line-color': green[60], 'line-width': 2 }}
          />
          <Layer
            id="ec-cov-incomplete-line"
            type="line"
            source-layer={SOURCE_LAYER}
            filter={['all', isType('shape'), isState('incomplete')] as never}
            paint={{
              'line-color': gray[70],
              'line-width': 1.5,
              'line-dasharray': [3, 2],
            }}
          />
          <Layer
            id="ec-cov-empty-line"
            type="line"
            source-layer={SOURCE_LAYER}
            filter={['all', isType('shape'), isState('empty')] as never}
            paint={{
              'line-color': red[60],
              'line-width': 1.5,
              'line-dasharray': [3, 2],
            }}
          />
          <Layer
            id="ec-cov-highlight-line"
            type="line"
            source-layer={SOURCE_LAYER}
            filter={highlightFilter as never}
            paint={{ 'line-color': blue[70], 'line-width': 2.5 }}
          />
          <Layer
            id="ec-cov-label"
            type="symbol"
            source-layer={SOURCE_LAYER}
            filter={isType('centroid') as never}
            layout={{
              'text-field': ['get', 'name'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 3, 9, 8, 12],
              'text-allow-overlap': false,
              'text-ignore-placement': false,
              'text-padding': 10,
            }}
            paint={{
              'text-color': gray[100],
              'text-halo-color': white,
              'text-halo-width': 1,
            }}
          />
        </Source>
      </Map>
      <ul className={styles.legend} aria-label="Coverage legend">
        <li className={styles.legendItem}>
          <span className={styles.swatchComplete} /> Complete: where every
          assigned scenario reaches
        </li>
        <li className={styles.legendItem}>
          <span className={styles.swatchIncomplete} /> Incomplete: the assigned
          scenarios&apos; areas
        </li>
        <li className={styles.legendItem}>
          <span className={styles.swatchEmpty} /> Complete, but with no common
          area to draw
        </li>
      </ul>
    </div>
  );
};

export { EvaluationCasesCoverageMapStrip };
